import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../services/hooks";
import { isAuthSelector } from "../services/selectors/auth.selectors";
import {
  callStateSelector,
  callDurationSelector,
  currentCallSelector,
} from "../services/selectors/callCenter.selectors";
import {
  createOutgoingCallId, 
  paycallSoftphone,
} from "../services/paycall/paycallSoftphone.service";
import type {
  TOutgoingDialMeta,
  TSipConnectionStatus,
} from "../services/paycall/paycall.types";
import {
  endActiveCallWidget,
  startOutgoingCall,
  applyMvoiceCallEnded,
  setOutboundRemoteAnswered,
} from "../services/slices/callCenter.slice";

type TPaycallContextValue = {
  sipStatus: TSipConnectionStatus;
  answerSipCall: () => Promise<void>;
  rejectSipCall: () => Promise<void>;
  hangup: () => Promise<void>;
  dial: (phone: string, meta?: TOutgoingDialMeta) => Promise<void>;
  myExtension: string | null;
  currentSipCallId: string | null;
  /** Outbound dial — true once the remote party picks up (SIP Established). */
  outboundRemoteAnswered: boolean;
};

const PaycallContext = createContext<TPaycallContextValue | null>(null);

export const PaycallProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(isAuthSelector);
  const currentCall = useAppSelector(currentCallSelector);
  const callState = useAppSelector(callStateSelector);
  const callDuration = useAppSelector(callDurationSelector);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const currentCallRef = useRef(currentCall);
  const callStateRef = useRef(callState);
  const callDurationRef = useRef(callDuration);

  useEffect(() => {
    currentCallRef.current = currentCall;
    callStateRef.current = callState;
    callDurationRef.current = callDuration;
  }, [currentCall, callState, callDuration]);
  const [sipStatus, setSipStatus] = useState<TSipConnectionStatus>("disconnected");
  const [myExtension, setMyExtension] = useState<string | null>(null);
  const [currentSipCallId, setCurrentSipCallId] = useState<string | null>(null);
  const [outboundRemoteAnswered, setOutboundRemoteAnsweredState] = useState(false);

  useEffect(() => {
    return paycallSoftphone.onStatusChange((status) => {
      setSipStatus(status);
      setMyExtension(paycallSoftphone.getMyExtension());
      setCurrentSipCallId(paycallSoftphone.getCurrentCallId());
    });
  }, []);

  useEffect(() => {
    return paycallSoftphone.onRemoteAnsweredChange((answered) => {
      setOutboundRemoteAnsweredState(answered);
      dispatch(setOutboundRemoteAnswered(answered));
    });
  }, [dispatch]);

  useEffect(() => {
    return paycallSoftphone.onSessionEnded(() => {
      setCurrentSipCallId(null);
      setOutboundRemoteAnsweredState(false);

      const call = currentCallRef.current;
      const state = callStateRef.current;
      if (!call || state !== "active") {
        return;
      }

      // Active local SIP session ended — show ended animation.
      // Ringing inbound is cleared by PaycallCallEnd / reject / CallTaken over SignalR.
      const now = new Date().toISOString();
      dispatch(
        applyMvoiceCallEnded({
          callId: call.callId,
          direction: call.isOutbound ? "Outbound" : "Inbound",
          duration: callDurationRef.current,
          startTime: now,
          endTime: now,
          wasRecorded: false,
        })
      );
    });
  }, [dispatch]);

  useEffect(() => {
    paycallSoftphone.bindDispatch(dispatch);

    if (!isAuth) {
      void paycallSoftphone.disconnect();
      return () => {
        void paycallSoftphone.disconnect();
      };
    }

    void paycallSoftphone.connect();

    return () => {
      void paycallSoftphone.disconnect();
    };
  }, [dispatch, isAuth]);

  const answerSipCall = useCallback(async () => {
    await paycallSoftphone.answerSipCall();
    setCurrentSipCallId(paycallSoftphone.getCurrentCallId());
  }, []);
  const rejectSipCall = useCallback(() => paycallSoftphone.rejectSipCall(), []);

  const hangup = useCallback(async () => {
    await paycallSoftphone.hangup();
  }, []);

  const dial = useCallback(
    async (phone: string, meta?: TOutgoingDialMeta) => {
      const trimmed = phone.trim();
      if (!trimmed) return;

      const callId = createOutgoingCallId();
      setOutboundRemoteAnsweredState(false);
      dispatch(setOutboundRemoteAnswered(false));
      dispatch(
        startOutgoingCall({
          callId,
          phone: trimmed,
          userId: meta?.userId,
          guid: meta?.guid,
          firstName: meta?.firstName,
          lastName: meta?.lastName,
        })
      );

      try {
        await paycallSoftphone.dial(trimmed);
        setCurrentSipCallId(paycallSoftphone.getCurrentCallId());
      } catch (e) {
        dispatch(endActiveCallWidget());
        throw e;
      }
    },
    [dispatch]
  );

  return (
    <PaycallContext.Provider
      value={{
        sipStatus,
        answerSipCall,
        rejectSipCall,
        hangup,
        dial,
        myExtension,
        currentSipCallId,
        outboundRemoteAnswered,
      }}
    >
      <audio
        ref={(el) => {
          remoteAudioRef.current = el;
          paycallSoftphone.setRemoteAudioElement(el);
        }}
        autoPlay
        playsInline
        style={{
          position: "fixed",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
        aria-hidden
      />
      {children}
    </PaycallContext.Provider>
  );
};

export function usePaycall(): TPaycallContextValue {
  const ctx = useContext(PaycallContext);
  if (!ctx) {
    throw new Error("usePaycall must be used within PaycallProvider");
  }
  return ctx;
}
